import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ListenerRole } from '../../../../hooks/usePeers';
import { useUpdateUser } from '../../../../hooks/useUsers';
import { SystemMessage, useSystemMessages } from '../../../../hooks/useComms';
import Modal from '../../../../components/common/modal';
import { toast } from 'react-toastify';

interface RejectionModalData {
  templateId: number | null;
  message: string;
  sendMessage: boolean;
}

interface AboutMeReviewProps {
  peerData: ListenerRole;
  refetchPeerData: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const MAX_CHARACTERS = 1000;

const AboutMeReview: React.FC<AboutMeReviewProps> = ({ peerData, refetchPeerData, onDirtyChange }) => {
  const [currentAboutMe, setCurrentAboutMe] = useState('');
  const [proposedAboutMe, setProposedAboutMe] = useState('');
  const [proposedStatus, setProposedStatus] = useState<string>('pending');
  const initializedRef = useRef(false);
  const savedCurrentAboutMeRef = useRef('');
  const savedProposedAboutMeRef = useRef('');
  const savedProposedStatusRef = useRef('pending');

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const current = peerData.about_me || '';
    const proposed = peerData.proposed_about_me || '';
    let status: string;
    if (peerData.about_me_rejected) {
      status = 'rejected';
    } else if (peerData.proposed_about_me === peerData.about_me && peerData.proposed_about_me) {
      status = 'approved';
    } else {
      status = 'pending';
    }

    setCurrentAboutMe(current);
    setProposedAboutMe(proposed);
    setProposedStatus(status);

    savedCurrentAboutMeRef.current = current;
    savedProposedAboutMeRef.current = proposed;
    savedProposedStatusRef.current = status;
  }, [peerData]);
  useEffect(() => {
    if (!initializedRef.current) return;
    const isDirty =
      currentAboutMe !== savedCurrentAboutMeRef.current ||
      proposedAboutMe !== savedProposedAboutMeRef.current ||
      proposedStatus !== savedProposedStatusRef.current;
    onDirtyChange?.(isDirty);
  }, [currentAboutMe, proposedAboutMe, proposedStatus, onDirtyChange]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionModalData, setRejectionModalData] = useState<RejectionModalData>({
    templateId: null,
    message: '',
    sendMessage: true,
  });

  const { updateUser: updateUserCurrent } = useUpdateUser();
  const { updateUser: updateUserProposed } = useUpdateUser();
  const { data: systemMessages } = useSystemMessages();

  const aboutMeRejectedMessages = useMemo(
    () => systemMessages?.filter((item: SystemMessage) => item.group?.key === 'about_me_rejected') ?? [],
    [systemMessages],
  );

  const currentCharCount = currentAboutMe.length;
  const proposedCharCount = proposedAboutMe.length;
  const isCurrentOverLimit = currentCharCount > MAX_CHARACTERS;
  const isProposedOverLimit = proposedCharCount > MAX_CHARACTERS;

  const handleSaveCurrent = () => {
    if (isCurrentOverLimit) {
      toast.error('Current about me exceeds character limit');
      return;
    }

    updateUserCurrent.mutate(
      { userId: peerData.user.id, data: { listener_role: { about_me: currentAboutMe } } },
      {
        onSuccess: () => {
          toast.success('Current about me updated successfully');
          savedCurrentAboutMeRef.current = currentAboutMe;
          onDirtyChange?.(false);
          initializedRef.current = false;
          refetchPeerData();
        },
        onError: () => toast.error('Failed to update current about me'),
      },
    );
  };

  const handleSaveProposed = () => {
    if (isProposedOverLimit) {
      toast.error('Proposed about me exceeds character limit');
      return;
    }

    if (proposedStatus === 'rejected') {
      const firstMsg = aboutMeRejectedMessages[0];
      setRejectionModalData({
        templateId: firstMsg?.id ?? null,
        message: firstMsg?.message ?? '',
        sendMessage: firstMsg != null,
      });
      setShowRejectModal(true);
      return;
    }

    submitProposedUpdate();
  };

  const submitProposedUpdate = (overrideListenerRole?: Record<string, any>) => {
    let listenerRole: Record<string, any>;

    if (proposedStatus === 'approved') {
      listenerRole = { about_me: proposedAboutMe, proposed_about_me: null, about_me_rejected: false };
    } else if (proposedStatus === 'rejected') {
      listenerRole = overrideListenerRole ?? { about_me_rejected: true };
    } else {
      listenerRole = { proposed_about_me: proposedAboutMe, about_me_rejected: false };
    }

    updateUserProposed.mutate(
      { userId: peerData.user.id, data: { listener_role: listenerRole } },
      {
        onSuccess: () => {
          toast.success(`Proposed about me ${proposedStatus}`);
          savedProposedAboutMeRef.current = proposedAboutMe;
          savedProposedStatusRef.current = proposedStatus;
          onDirtyChange?.(false);
          setShowRejectModal(false);
          initializedRef.current = false;
          refetchPeerData();
        },
        onError: () => toast.error('Failed to update proposed about me'),
      },
    );
  };

  const handleRejectConfirm = () => {
    const listenerRole: Record<string, any> = {
      about_me_rejected: true,
      ...(rejectionModalData.sendMessage && rejectionModalData.templateId
        ? {
            system_message_id: rejectionModalData.templateId,
            message: rejectionModalData.message,
          }
        : {}),
    };
    submitProposedUpdate(listenerRole);
  };

  return (
    <>
      <Modal
        isModel={showRejectModal}
        InnerComponent={
          <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">Reject About Me</h3>

              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Proposed About Me</label>
                <textarea
                  readOnly
                  className="h-28 w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  value={proposedAboutMe}
                />
              </div>

              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Rejection Message Template</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={rejectionModalData.templateId ?? 'none'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'none') {
                      setRejectionModalData((prev) => ({ ...prev, templateId: null, message: '', sendMessage: false }));
                    } else {
                      const id = Number(val);
                      const msg = aboutMeRejectedMessages.find((m: SystemMessage) => m.id === id);
                      setRejectionModalData((prev) => ({
                        ...prev,
                        templateId: id,
                        message: msg?.message ?? '',
                        sendMessage: true,
                      }));
                    }
                  }}
                >
                  <option value="none">Do NOT send message</option>
                  {aboutMeRejectedMessages.map((m: SystemMessage) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {rejectionModalData.sendMessage && (
                <div className="mb-6">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    className="h-32 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={rejectionModalData.message}
                    onChange={(e) => setRejectionModalData((prev) => ({ ...prev, message: e.target.value }))}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={updateUserProposed.isLoading}
                  className="bg-red rounded px-4 py-2 text-sm font-semibold text-white opacity-100 hover:opacity-90 disabled:opacity-50"
                  onClick={handleRejectConfirm}
                >
                  {updateUserProposed.isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        }
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">Current</p>
          <textarea
            value={currentAboutMe}
            onChange={(e) => setCurrentAboutMe(e.target.value)}
            className={`mb-2 max-h-48 min-h-32 w-full resize-y overflow-y-auto rounded border px-3 py-2 text-sm ${
              isCurrentOverLimit ? 'border-red' : 'border-gray-300'
            }`}
            style={isCurrentOverLimit ? { backgroundColor: '#fef2f2' } : {}}
            placeholder="Current about me text..."
          />
          <div className={`mb-3 text-xs ${isCurrentOverLimit ? 'text-red font-semibold' : 'text-gray-500'}`}>
            {currentCharCount} / {MAX_CHARACTERS} characters
            {isCurrentOverLimit && <span className="ml-2">⚠ Exceeds limit</span>}
          </div>
          <button
            onClick={handleSaveCurrent}
            disabled={isCurrentOverLimit || updateUserCurrent.isLoading}
            className="rounded bg-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {updateUserCurrent.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">Proposed</p>
          <textarea
            value={proposedAboutMe}
            onChange={(e) => setProposedAboutMe(e.target.value)}
            className={`mb-2 max-h-48 min-h-32 w-full resize-y overflow-y-auto rounded border px-3 py-2 text-sm ${
              isProposedOverLimit ? 'border-red' : 'border-gray-300'
            }`}
            style={isProposedOverLimit ? { backgroundColor: '#fef2f2' } : {}}
            placeholder="Proposed about me text..."
          />
          <div className={`mb-3 text-xs ${isProposedOverLimit ? 'text-red font-semibold' : 'text-gray-500'}`}>
            {proposedCharCount} / {MAX_CHARACTERS} characters
            {isProposedOverLimit && <span className="ml-2">⚠ Exceeds limit</span>}
          </div>

          <div className="mb-3">
            <select
              value={proposedStatus}
              onChange={(e) => setProposedStatus(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="pending">Needs Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <button
            onClick={handleSaveProposed}
            disabled={isProposedOverLimit || updateUserProposed.isLoading}
            className="rounded bg-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {updateUserProposed.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AboutMeReview;
